// src/components/JobView/TaskAgentModal.js
// Asistente/Agente de Tareas: además de responder, PUEDE ENVIAR CORREOS a los
// usuarios (alertar/sugerir/notificar). Llama a la Cloud Function taskAgent.
import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Bot, User, Mail } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';

// Renderiza texto con **negritas** y saltos de línea (markdown ligero).
const RichText = ({ text }) => {
    const lines = String(text || '').split('\n');
    return (
        <div className="text-sm leading-relaxed space-y-1">
            {lines.map((line, li) => {
                if (line.trim() === '') return <div key={li} className="h-1" />;
                const parts = line.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
                return (
                    <p key={li}>
                        {parts.map((p, pi) =>
                            p.startsWith('**') && p.endsWith('**')
                                ? <strong key={pi}>{p.slice(2, -2)}</strong>
                                : <span key={pi}>{p}</span>
                        )}
                    </p>
                );
            })}
        </div>
    );
};

const TaskAgentModal = ({ isOpen, onClose, jobId, jobName, user }) => {
    const functions = getFunctions(getApp(), 'us-central1');
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const endRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            setMessages([{ sender: 'ai', text: `¡Hola ${user?.displayName || user?.email || ''}! Soy el Asistente de Tareas de ${jobName || jobId}. Puedo analizar las tareas y, si me lo pides, enviar correos a los responsables para alertar, sugerir o notificar.` }]);
        }
    }, [isOpen, jobId, jobName, user]);

    useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

    const handleSend = async (e) => {
        e.preventDefault();
        const text = input.trim();
        if (!text || isLoading) return;

        const history = messages.map((m) => ({ role: m.sender === 'user' ? 'user' : 'model', content: m.text }));
        setMessages((prev) => [...prev, { sender: 'user', text }]);
        setInput('');
        setIsLoading(true);
        try {
            const taskAgent = httpsCallable(functions, 'taskAgent');
            const result = await taskAgent({ jobId, userMessage: text, history, callerEmail: user?.email || null });
            setMessages((prev) => [...prev, {
                sender: 'ai',
                text: result.data.response || 'Listo.',
                emailsSent: result.data.emailsSent || [],
            }]);
        } catch (error) {
            console.error('Error taskAgent:', error);
            setMessages((prev) => [...prev, { sender: 'ai', text: `Error de conexión: ${error.message}` }]);
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex justify-center items-center p-4">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl h-[75vh] flex flex-col">
                <header className="p-4 border-b flex justify-between items-center bg-gray-50 rounded-t-lg">
                    <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                        <Bot size={20} className="text-emerald-600" /> Asistente de Tareas {jobId ? `(${jobId})` : ''}
                    </h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-800"><X size={24} /></button>
                </header>
                <main className="flex-grow p-4 overflow-y-auto bg-gray-100">
                    <div className="space-y-4">
                        {messages.map((msg, index) => (
                            <div key={index} className={`flex items-start gap-3 ${msg.sender === 'user' ? 'justify-end' : ''}`}>
                                {msg.sender === 'ai' && <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center text-white flex-shrink-0"><Bot size={18} /></div>}
                                <div className={`px-4 py-2.5 rounded-2xl max-w-lg shadow-sm ${msg.sender === 'user' ? 'bg-blue-500 text-white rounded-br-sm' : 'bg-white text-gray-800 rounded-bl-sm'}`}>
                                    {msg.sender === 'user'
                                        ? <p className="text-sm" style={{ whiteSpace: 'pre-wrap' }}>{msg.text}</p>
                                        : <RichText text={msg.text} />}
                                    {msg.emailsSent && msg.emailsSent.length > 0 && (
                                        <div className="mt-2.5 pt-2.5 border-t border-gray-100 space-y-1">
                                            {msg.emailsSent.map((m, i) => (
                                                <p key={i} className="text-xs text-emerald-700 flex items-center gap-1.5 bg-emerald-50 rounded px-2 py-1">
                                                    <Mail size={13} className="flex-shrink-0" /> Enviado a <strong>{m.nombre || m.to}</strong>
                                                </p>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {msg.sender === 'user' && <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white flex-shrink-0"><User size={18} /></div>}
                            </div>
                        ))}
                        {isLoading && (
                            <div className="flex items-start gap-3">
                                <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center text-white flex-shrink-0"><Bot size={18} /></div>
                                <div className="p-3 rounded-lg bg-white text-gray-800"><p className="text-sm italic">Analizando y actuando...</p></div>
                            </div>
                        )}
                        <div ref={endRef} />
                    </div>
                </main>
                <footer className="p-4 border-t bg-white rounded-b-lg">
                    <form onSubmit={handleSend} className="flex items-center gap-2">
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Ej: avísale por correo a Daniel que su tarea está atrasada"
                            className="w-full p-2 border border-gray-300 rounded-lg"
                            disabled={isLoading}
                        />
                        <button type="submit" className="p-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-400" disabled={isLoading || !input.trim()}>
                            <Send size={20} />
                        </button>
                    </form>
                </footer>
            </div>
        </div>
    );
};

export default TaskAgentModal;
